import { DEFAULT_CONFIG } from './presets';
import type { GeneratorConfig, PatternMetrics, PhysicalSize, Resolution } from './types';

export const MAX_CANVAS_DIMENSION = 32_767;
export const MAX_CANVAS_PIXELS = 268_435_456;

export function cloneDefaultConfig(): GeneratorConfig {
  return structuredClone(DEFAULT_CONFIG);
}

export function getPatternMetrics(config: GeneratorConfig): PatternMetrics {
  if (config.mode === 'projection') {
    const resolution = sanitizeResolution(config.projection.resolution);
    const physical = sanitizePhysical(config.projection.physical);
    return createPatternMetrics(resolution, physical);
  }

  const cabinet = sanitizeResolution(config.led.cabinetResolution);
  const columns = positiveInteger(config.led.columns);
  const rows = positiveInteger(config.led.rows);
  const resolution = {
    width: safeMultiply(cabinet.width, columns),
    height: safeMultiply(cabinet.height, rows),
  };
  const physical = {
    widthMm: positiveNumber(config.led.cabinetPhysical.widthMm) * columns,
    heightMm: positiveNumber(config.led.cabinetPhysical.heightMm) * rows,
  };
  return createPatternMetrics(resolution, physical);
}

export function formatPhysicalSize(physical: PhysicalSize, unit: 'mm' | 'm'): string {
  if (unit === 'm') {
    return `${formatNumber(physical.widthMm / 1000)} × ${formatNumber(physical.heightMm / 1000)} m`;
  }
  return `${formatNumber(physical.widthMm)} × ${formatNumber(physical.heightMm)} mm`;
}

export function buildFileName(config: GeneratorConfig): string {
  const { resolution, physical } = getPatternMetrics(config);
  const title = sanitizeFileSegment(config.title || 'ScreenGrid');
  const physicalPart = `${formatFileNumber(physical.widthMm)}x${formatFileNumber(physical.heightMm)}mm`;
  return `${title}_${physicalPart}_${resolution.width}x${resolution.height}.png`;
}

export function validateConfig(input: unknown): GeneratorConfig {
  if (!input || typeof input !== 'object') {
    throw new Error('The configuration must be a JSON object.');
  }
  const value = input as Partial<GeneratorConfig>;
  if (value.schemaVersion !== 1) {
    throw new Error('Unsupported configuration version.');
  }
  if (value.mode !== 'projection' && value.mode !== 'led') {
    throw new Error('Invalid generator mode.');
  }
  const merged = mergeConfig(cloneDefaultConfig(), value);
  assertValidConfig(merged);
  getPatternMetrics(merged);
  return merged;
}

export function assertValidConfig(config: GeneratorConfig): void {
  limitedString(config.title, 'Screen name', 120);
  if (config.displayUnit !== 'mm' && config.displayUnit !== 'm') {
    throw new Error('Invalid physical size unit.');
  }

  positiveInteger(config.projection.resolution.width);
  positiveInteger(config.projection.resolution.height);
  assertCanvasResolution(config.projection.resolution);
  positiveNumber(config.projection.physical.widthMm);
  positiveNumber(config.projection.physical.heightMm);
  limitedString(config.projection.colorPreset, 'Projection color preset', 64);
  positiveNumber(config.projection.gridStepMm);
  boundedInteger(config.projection.gridLineWidth, 1, 32, 'Projection grid line width');
  boundedNumber(config.projection.overlayLineWidth, 1, 32, 'Projection overlay line width');
  hexColor(config.projection.gridColor, 'Projection grid color');
  hexColor(config.projection.backgroundColorA, 'Projection background color A');
  hexColor(config.projection.backgroundColorB, 'Projection background color B');
  hexColor(config.projection.overlayColor, 'Projection overlay color');
  if (!['solid', 'checkerboard', 'transparent'].includes(config.projection.backgroundMode)) {
    throw new Error('Invalid projection background mode.');
  }
  booleanValue(config.projection.showDiagonals, 'Projection diagonals');
  booleanValue(config.projection.showCenter, 'Projection center cross');
  booleanValue(config.projection.showCircle, 'Projection circle');

  positiveInteger(config.led.cabinetResolution.width);
  positiveInteger(config.led.cabinetResolution.height);
  positiveNumber(config.led.cabinetPhysical.widthMm);
  positiveNumber(config.led.cabinetPhysical.heightMm);
  positiveInteger(config.led.columns);
  positiveInteger(config.led.rows);
  assertCanvasResolution({
    width: safeMultiply(config.led.cabinetResolution.width, config.led.columns),
    height: safeMultiply(config.led.cabinetResolution.height, config.led.rows),
  });
  limitedString(config.led.colorPreset, 'LED color preset', 64);
  hexColor(config.led.gridColor, 'LED grid color');
  hexColor(config.led.backgroundColorA, 'LED checker color A');
  hexColor(config.led.backgroundColorB, 'LED checker color B');
  hexColor(config.led.coordinateColor, 'LED coordinate color');
  booleanValue(config.led.showCoordinates, 'LED cabinet coordinates');

  hexColor(config.power.lineColor, 'Power line color');
  boundedInteger(config.power.lineWidth, 2, 80, 'Power line width');
  booleanValue(config.power.incoming, 'Incoming power cable');
  limitedString(config.power.cableLabel, 'Incoming cable number', 32);
  if (!Array.isArray(config.power.routes)) throw new Error('Power routes must be an array.');
  config.power.routes.forEach((route, routeIndex) => {
    if (!route || typeof route !== 'object') throw new Error('Invalid power route.');
    limitedString(route.id, `Power route ${routeIndex + 1} ID`, 128, true);
    hexColor(route.color, `Power route ${routeIndex + 1} color`);
    boundedInteger(route.lineWidth, 2, 80, `Power route ${routeIndex + 1} line width`);
    booleanValue(route.incoming, `Power route ${routeIndex + 1} incoming state`);
    limitedString(route.cableLabel, `Power route ${routeIndex + 1} cable number`, 32);
    if (!Array.isArray(route.points)) throw new Error('Power route points must be an array.');
    route.points.forEach((point) => {
      if (!point || typeof point !== 'object') throw new Error('Invalid power route point.');
      nonNegativeInteger(point.column, 'Power route column');
      nonNegativeInteger(point.row, 'Power route row');
    });
  });

  booleanValue(config.label.enabled, 'Center label state');
  hexColor(config.label.textColor, 'Center label text color');
  boundedInteger(config.label.fontSize, 10, 500, 'Center label font size');
  hexColor(config.label.backgroundColor, 'Center label background color');
  boundedNumber(config.label.backgroundOpacity, 0, 1, 'Center label background opacity');

  boundedNumber(config.logo.scale, 0.02, 0.3, 'Logo size');
  boundedNumber(config.logo.opacity, 0, 1, 'Logo opacity');
  nullableString(config.logo.fileName, 'Logo file name', 255);
  nullableString(config.logo.dataUrl, 'Logo data', 40_000_000);
  if (config.logo.dataUrl && !config.logo.dataUrl.startsWith('data:image/png;base64,')) {
    throw new Error('Logo data must contain a Base64 PNG image.');
  }
}

export function mergeConfig(
  base: GeneratorConfig,
  value: Partial<GeneratorConfig>,
): GeneratorConfig {
  return {
    ...base,
    ...value,
    projection: {
      ...base.projection,
      ...value.projection,
      resolution: { ...base.projection.resolution, ...value.projection?.resolution },
      physical: { ...base.projection.physical, ...value.projection?.physical },
    },
    led: {
      ...base.led,
      ...value.led,
      cabinetResolution: {
        ...base.led.cabinetResolution,
        ...value.led?.cabinetResolution,
      },
      cabinetPhysical: { ...base.led.cabinetPhysical, ...value.led?.cabinetPhysical },
    },
    power: {
      ...base.power,
      ...value.power,
      routes:
        value.power && Object.prototype.hasOwnProperty.call(value.power, 'routes')
          ? value.power.routes!
          : base.power.routes,
    },
    label: { ...base.label, ...value.label },
    logo: { ...base.logo, ...value.logo },
    schemaVersion: 1,
  };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 ** 2) return `${Math.ceil(bytes / 1024)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

function sanitizeResolution(resolution: Resolution): Resolution {
  return {
    width: positiveInteger(resolution.width),
    height: positiveInteger(resolution.height),
  };
}

function sanitizePhysical(physical: PhysicalSize): PhysicalSize {
  return {
    widthMm: positiveNumber(physical.widthMm),
    heightMm: positiveNumber(physical.heightMm),
  };
}

function positiveInteger(value: number): number {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new Error('Dimensions and counts must be positive whole numbers.');
  }
  return value;
}

function positiveNumber(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error('Physical dimensions must be positive numbers.');
  }
  return value;
}

function safeMultiply(left: number, right: number): number {
  const result = left * right;
  if (!Number.isSafeInteger(result)) {
    throw new Error('The calculated resolution is too large.');
  }
  return result;
}

function estimateCanvasBytes(resolution: Resolution): number {
  return safeMultiply(safeMultiply(resolution.width, resolution.height), 4);
}

function createPatternMetrics(resolution: Resolution, physical: PhysicalSize): PatternMetrics {
  assertCanvasResolution(resolution);
  return { resolution, physical, estimatedBytes: estimateCanvasBytes(resolution) };
}

function assertCanvasResolution(resolution: Resolution): void {
  const pixels = safeMultiply(resolution.width, resolution.height);
  if (resolution.width > MAX_CANVAS_DIMENSION || resolution.height > MAX_CANVAS_DIMENSION) {
    throw new Error(`The final resolution cannot exceed ${MAX_CANVAS_DIMENSION} px per side.`);
  }
  if (pixels > MAX_CANVAS_PIXELS) {
    throw new Error('The final resolution is too large for a browser canvas.');
  }
}

function boundedInteger(value: number, min: number, max: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < min || value > max) {
    throw new Error(`${label} must be a whole number from ${min} to ${max}.`);
  }
}

function nonNegativeInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative whole number.`);
  }
}

function boundedNumber(value: number, min: number, max: number, label: string): void {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new Error(`${label} must be from ${min} to ${max}.`);
  }
}

function booleanValue(value: boolean, label: string): void {
  if (typeof value !== 'boolean') throw new Error(`${label} must be true or false.`);
}

function hexColor(value: string, label: string): void {
  if (typeof value !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(value)) {
    throw new Error(`${label} must use the format #RRGGBB.`);
  }
}

function limitedString(value: string, label: string, maxLength: number, required = false): void {
  if (typeof value !== 'string' || value.length > maxLength || (required && !value.trim())) {
    throw new Error(
      `${label} must be ${required ? 'a non-empty string' : 'text'} up to ${maxLength} characters.`,
    );
  }
}

function nullableString(value: string | null, label: string, maxLength: number): void {
  if (value !== null) limitedString(value, label, maxLength);
}

function sanitizeFileSegment(value: string): string {
  return (
    value
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'ScreenGrid'
  );
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 }).format(value);
}

function formatFileNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
}
